import { Component, computed, EventEmitter, Input, OnInit, Output, Signal, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StructuredProcessWorkplanStatusEnum, StructuredProcessArtifactType, StructuredProcessArtifactWorkplan, StructuredProcessWorkplan_Milestone, StructuredProcessWorkplan_Task } from 'src/app/moov-common/services/chat/structuredProcess-artifactData';
import { StructuredProcessService } from 'src/app/moov-common/services/chat/structured-process.service';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { GenderPipe } from 'src/app/moov-common/pipes/gender.pipe';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { TranslateService } from '@ngx-translate/core';
import { AbstractStructuredProcessService } from 'src/app/moov-common/services/chat/structured-process.abstract.service';
import { StructuredProcessStateService } from 'src/app/moov-common/services/chat/structured-process-state.service';

@Component({
  selector: 'app-structured-process-artifact-view-workplan',
  templateUrl: './structured-process-artifact-view-workplan.component.html',
  styleUrl: './structured-process-artifact-view-workplan.component.scss'
})
export class StructuredProcessArtifactViewWorkplanComponent extends BaseComponent implements OnInit {
    // ------- DEMO DATA (fits your structure) -------
    workplan = signal<StructuredProcessArtifactWorkplan | null>(null);
    //   id: 'plan-001',
    //   userId: 'user-123',
    //   structuredProcessId: 'process-abc',
    //   type: StructuredProcessArtifactType.WORKPLAN,
    //   name: 'Leadership Development Plan',
    //   description: 'Establish and implement advanced leadership practices.',
    //   dueDate: iso(new Date(new Date().getFullYear(), 8, 2)), // Sep 2
    //   updatedAt: null,
    //   milestones: [
    //     {
    //       idx: 1,
    //       name: 'Establish Leadership Framework',
    //       description: 'Define leadership practices for managing managers',
    //       dueDate: iso(new Date(new Date().getFullYear(), 5, 24)),
    //       updatedAt: null,
    //       completedAt: null,
    //       status: 'IN_PROGRESS',
    //       tasks: [
    //         { idx: 1, name: 'Draft leadership principles', description: "Request structured feedback from direct reports and peers on leadership, control, and consistency.", dueDate: isoD(6, 13), updatedAt: null, completedAt: null, status: 'PENDING' },
    //         { idx: 2, name: 'Schedule mentorship with Gina', description: "Create detailed org chart, highlight bottlenecks and management layers needed for growth.", dueDate: isoD(6, 17), updatedAt: null, completedAt: null, status: 'PENDING' },
    //         { idx: 3, name: 'Align with organizational values', description: "Discuss scaling team management and leadership coaching strategies.", dueDate: isoD(6, 20), updatedAt: null, completedAt: null, status: 'PENDING' },
    //       ]
    //     },
    //     {
    //       idx: 2,
    //       name: 'Deepen Professional Expertise',
    //       description: null,
    //       dueDate: isoD(7, 22),
    //       updatedAt: null,
    //       completedAt: null,
    //       status: 'PENDING',
    //       tasks: [
    //         { idx: 1, name: 'Enroll in advanced workshop', description: null, dueDate: null, updatedAt: null, completedAt: null, status: 'PENDING' },
    //         { idx: 2, name: 'Shadow senior leader', description: null, dueDate: null, updatedAt: null, completedAt: null, status: 'PENDING' },
    //         { idx: 3, name: 'Publish internal write-up', description: null, dueDate: null, updatedAt: null, completedAt: null, status: 'PENDING' },
    //       ]
    //     },
    //     {
    //       idx: 3,
    //       name: 'Demonstrate Scaled Impact',
    //       description: null,
    //       dueDate: isoD(9, 2),
    //       updatedAt: null,
    //       completedAt: null,
    //       status: 'PENDING',
    //       tasks: [
    //         { idx: 1, name: 'Lead cross-team initiative', description: null, dueDate: null, updatedAt: null, completedAt: null, status: 'PENDING' },
    //         { idx: 2, name: 'Present outcomes to execs', description: null, dueDate: null, updatedAt: null, completedAt: null, status: 'PENDING' },
    //       ]
    //     }
    //   ]
    // });
  
    // simple expanded-state map by milestone idx
    panels = signal<PanelState>({ });
  
    // UI state
    @Input() processId: string = '';
    @Input() isGeneratingArtifact: boolean = true;
    @Input() isFirstTimeArtifactCreation: boolean = false;

    isSaving = signal(false);
    dirty = signal(false);
    isTaskEdited = signal(false);
    isTaskDeleted = signal(false);
    edittedTask = signal<{ mIdx: number; tIdx: number } | null>(null);
    deletedTask = signal<{ mIdx: number; tIdx: number } | null>(null);
    draftName = signal<string>('');
    draftDescription = signal<string>('');
    emptyStateSteps :{ emoji: string; text: string }[] = []; 
    structuredProcessWorkplanStatusEnumInstance = StructuredProcessWorkplanStatusEnum;
    
    // ---- Computed progress ----
    totalMilestones = computed(() => this.workplan()?.milestones.length ?? 0);
    completedMilestones = computed(() =>
      this.workplan()?.milestones.filter(m => m.tasks.length > 0 && m.tasks.every(t => t.status === this.structuredProcessWorkplanStatusEnumInstance.DONE)).length ?? 0
    );
    milestoneProgressPct = computed(() => percent(this.completedMilestones(), this.totalMilestones()));
  
    totalTasks = computed(() => this.workplan()?.milestones.reduce((a, m) => a + m.tasks.length, 0) ?? 0);
    completedTasks = computed(() => this.workplan()?.milestones.reduce((a, m) => a + m.tasks.filter(t => t.status === 'DONE').length, 0) ?? 0);
    taskProgressPct = computed(() => percent(this.completedTasks(), this.totalTasks()));
  
    constructor(private structuredProcessService: AbstractStructuredProcessService, 
                private snack: MatSnackBar,
                private genderSerivce: GenderPipe,
                private translate: TranslateService,
                private processStateService: StructuredProcessStateService) {
      super();
    }

    // Same behaviour as the sidebar "View Discussion" button: toggles back to the chat view
    onBackToChat(): void {
      this.processStateService.switchToArtifactClicked$.next();
    }

    ngOnInit(): void {
      super.ngOnInit();
      if (this.processId != ''){
        this.updateWorkplan();
      }

      this.emptyStateSteps = [
        { emoji: '🧠', text: this.translate.instant('structuredProcessView.workplan.emptyStateSteps.1') },
        { emoji: '🎯', text: this.translate.instant('structuredProcessView.workplan.emptyStateSteps.2') },
        { emoji: '⏰', text: this.translate.instant('structuredProcessView.workplan.emptyStateSteps.3') },
      ];
    }
  
    public updateWorkplan(){
      this.structuredProcessService.getStructuredProcessArtifact(StructuredProcessArtifactType.WORKPLAN).subscribe(res => {
        console.log(`updateWorkplan res: ${JSON.stringify(res)}`);
        if (res){          
          this.workplan.set(res as StructuredProcessArtifactWorkplan);
        this.initPanels();
        }
        else{
          this.workplan.set(null);
        }
      });
    }

    // ---- Task interactions ----
    toggleTaskDone(m: StructuredProcessWorkplan_Milestone, t: StructuredProcessWorkplan_Task) {
      const now = new Date();
      // if the old status was 'DONE' then we need to change it to 'PENDING' 
      const newStatus: StructuredProcessWorkplanStatusEnum = t.status === this.structuredProcessWorkplanStatusEnumInstance.DONE ? this.structuredProcessWorkplanStatusEnumInstance.PENDING : this.structuredProcessWorkplanStatusEnumInstance.DONE;

      t.status = newStatus;
      t.updatedAt = iso(now);
      t.completedAt = newStatus === this.structuredProcessWorkplanStatusEnumInstance.DONE ? iso(now) : null;
  
      // Update milestone status
      this.syncMilestoneStatus(m);
      this.markDirty();
    }
  
    onTaskDateChange(t: StructuredProcessWorkplan_Task, date: Date | null) {
      console.log(`onTaskDateChange task ${t.name} d Date changed to ${date}`);
      t.dueDate = date ? iso(date) : null;
      console.log(`onTaskDateChange t.dueDate is ${t.dueDate}`);
      t.updatedAt = iso(new Date());
      this.markDirty();
    }
  
    clearTaskDate(t: StructuredProcessWorkplan_Task, e: MouseEvent) {
      e.stopPropagation();
      t.dueDate = null;
      t.updatedAt = iso(new Date());
      this.markDirty();
    }
  
    // ---- Drag & drop ordering (per milestone) ----
    dropTask(event: CdkDragDrop<StructuredProcessWorkplan_Task[]>, m: StructuredProcessWorkplan_Milestone) {
      if (event.previousIndex === event.currentIndex) return;
      moveItemInArray(m.tasks, event.previousIndex, event.currentIndex);
      // reassign idx (1-based)
      m.tasks.forEach((t, i) => { t.idx = i + 1; t.updatedAt = iso(new Date()); });
      this.markDirty();
    }
    
    // ---- Save ----
    save() {
      // if (this.isSaving()) return;
      const wp = this.workplan();

      if (!wp) return;

      this.isSaving.set(true);
      const payload: StructuredProcessArtifactWorkplan = {
        ...wp,
        updatedAt: iso(new Date()),
        milestones: wp.milestones.map(ms => ({
          ...ms,
          updatedAt: iso(new Date()),
          tasks: ms.tasks.map(t => ({ ...t }))
        }))
      };
  
      console.log(`save payload: ${JSON.stringify(payload)}`);
      this.structuredProcessService.saveStructuredProcessArtifact(payload).subscribe(res => {
        this.isSaving.set(false);
        this.dirty.set(false);
        this.snack.open('Workplan saved', 'OK', { duration: 2000 });
      }, error => {
        this.isSaving.set(false);
        this.snack.open('Failed to save', 'Dismiss', { duration: 3000 });
      });
    }
  
    // ---- Helpers ----
    syncMilestoneStatus(m: StructuredProcessWorkplan_Milestone) {
      const allDone = m.tasks.length > 0 && m.tasks.every(t => t.status === 'DONE');
      const anyDone = m.tasks.some(t => t.status === 'DONE' || t.status === 'IN_PROGRESS');
  
      const next: StructuredProcessWorkplanStatusEnum = allDone ? this.structuredProcessWorkplanStatusEnumInstance.DONE : (anyDone ? this.structuredProcessWorkplanStatusEnumInstance.IN_PROGRESS : this.structuredProcessWorkplanStatusEnumInstance.PENDING);
  
      if (m.status !== next) {
        m.status = next;
        m.updatedAt = iso(new Date());
        m.completedAt = next === 'DONE' ? iso(new Date()) : null;
      }
    }

    initPanels(): void {
      const msList = this.workplan()?.milestones ?? [];
      const map: Record<number, boolean> = {};
    
      // collapse all
      for (const ms of msList) map[ms.idx] = false;
    
      // pick target to expand
      const target =
        msList.find(m => m.status === 'IN_PROGRESS') ??
        msList.find(m => m.status === 'PENDING') ??
        msList[0];
    
      if (target) map[target.idx] = true;
    
      this.panels.set(map);
    }
  
    isExpanded(idx: number) 
    { 
      return !!this.panels()[idx]; 
    }
  
    setExpanded(idx: number, val: boolean) {
      this.panels.set({ ...this.panels(), [idx]: val });
    } 

    onCalendarSelect(t: StructuredProcessWorkplan_Task, d: Date | null) {
      t.dueDate = d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString() : null;
      t.updatedAt = new Date().toISOString();
      this.markDirty();
    }

    markDirty() {
      // tasks are changed in place - re-emit the signal so the computed progress picks the change up
      this.workplan.update(wp => wp ? { ...wp } : wp);
      this.dirty.set(true);
      this.save();
    }
  
    trackMs = (_: number, m: StructuredProcessWorkplan_Milestone) => m.idx;
    trackTask = (_: number, t: StructuredProcessWorkplan_Task) => t.idx;

    doneCount(m: StructuredProcessWorkplan_Milestone): number {
      // fast and template-safe
      let n = 0;
      for (const t of m.tasks) if (t.status === 'DONE') n++;
      return n;
    }
    
    toDate(v?: string | null): Date | null {
      return v ? new Date(v) : null;
    }

    formatDate(date: Date): string{
      return "";
    }

    editTask(mIdx: number, tIdx: number) {
      console.log(`editTask mIdx: ${mIdx}, tIdx: ${tIdx}`);
      const task = this.findTask(mIdx, tIdx);
      if (!task) return;
    
      this.edittedTask.set({ mIdx, tIdx });
      this.draftName.set(task.name ?? '');
      this.draftDescription.set(task.description ?? '');
      this.isTaskEdited.set(true);

      console.log(`editTask edittedTask: ${JSON.stringify(task)}`);
    }

    private findTask(mIdx: number, tIdx: number) {
      const ms = this.workplan()?.milestones.find(m => m.idx === mIdx);
      return ms ? ms.tasks.find(t => t.idx === tIdx) : undefined;
    }
    
    isTaskIsEditted(mIdx: number, tIdx: number) {
      return !!this.edittedTask() && this.edittedTask()!.mIdx === mIdx && this.edittedTask()!.tIdx === tIdx;
    }   

    saveTaskEdit() {
      const e = this.edittedTask();
      if (!e) return;
      const task = this.findTask(e.mIdx, e.tIdx);
      if (!task) return;
    
      task.name = this.draftName().trim();
      task.description = this.draftDescription().trim() || null;
      task.updatedAt = new Date().toISOString();
    
      this.clearEditState();
      this.markDirty();
    }
    
    cancelTaskEdit() {
      this.clearEditState();
    }
    
    private clearEditState() {
      this.edittedTask.set(null);
      this.draftName.set('');
      this.draftDescription.set('');
      this.isTaskEdited.set(false);
    }

    addTask(m: StructuredProcessWorkplan_Milestone) {
      const newTask: StructuredProcessWorkplan_Task = {
        idx: m.tasks.length + 1,
        name: '',
        description: '',
        dueDate: null,
        updatedAt: null,
        completedAt: null,
        status: this.structuredProcessWorkplanStatusEnumInstance.PENDING,
      };

      m.tasks.push(newTask);
      this.editTask(m.idx, newTask.idx);
      this.markDirty();
    }

    isTaskIsDeleted(mIdx: number, tIdx: number) {
      return !!this.deletedTask() && this.deletedTask()!.mIdx === mIdx && this.deletedTask()!.tIdx === tIdx;
    }  

    deleteTask(mIdx: number, tIdx: number) {
      console.log(`deleteTask mIdx: ${mIdx}, tIdx: ${tIdx}`);
      const task = this.findTask(mIdx, tIdx);
      if (!task) return;
    
      this.deletedTask.set({ mIdx, tIdx });
      this.isTaskDeleted.set(true);

      console.log(`deleteTask deitedTask: ${JSON.stringify(task)}`);
    }  
    
    confirmTaskDeletion(){
        if (!this.deletedTask()) return;

        const { mIdx, tIdx } = this.deletedTask()!;
        const nowIso = new Date().toISOString();
      
        this.workplan.update(wp => {
          if (!wp) return null;

          const msPos = wp.milestones.findIndex(m => m.idx === mIdx);
          if (msPos === -1) return wp;
      
          const ms = wp?.milestones[msPos];
          const taskPos = ms.tasks.findIndex(t => t.idx === tIdx);
          if (taskPos === -1) return wp;
      
          // remove the task
          const tasks = ms.tasks.slice();
          tasks.splice(taskPos, 1);
      
          // reindex + bump updatedAt
          for (let i = 0; i < tasks.length; i++) {
            tasks[i] = { ...tasks[i], idx: i + 1, updatedAt: nowIso };
          }
      
          // recompute milestone status
          const allDone = tasks.length > 0 && tasks.every(t => t.status === 'DONE');
          const anyProgress = tasks.some(t => t.status !== 'PENDING');
          const status: StructuredProcessWorkplanStatusEnum = allDone ? this.structuredProcessWorkplanStatusEnumInstance.DONE : (anyProgress ? this.structuredProcessWorkplanStatusEnumInstance.IN_PROGRESS : this.structuredProcessWorkplanStatusEnumInstance.PENDING);
      
          const newMs = {
            ...ms,
            tasks,
            status,
            updatedAt: nowIso,
            completedAt: status === 'DONE' ? nowIso : null
          };
      
          const milestones = wp.milestones.slice();
          milestones[msPos] = newMs;
      
          return { ...wp, milestones, updatedAt: nowIso };
        });
      
        // if that task was being edited, exit edit mode
        this.deletedTask.set(null);
        this.isTaskDeleted.set(false);
        this.markDirty();
      }    

    computeStatus(){
      
    }

    cancelTaskDeletion(){
      this.deletedTask.set(null);
      this.isTaskDeleted.set(false);
    }

    downloadWorkplan(){
      this.structuredProcessService.exportStructuredProcessWorkplan(this.processId).subscribe((data: ArrayBuffer) => {
          const blob = new Blob(
            [data],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          );

          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = 'workplan.xlsx'; // or whatever name you want
          link.click();

          // Optional: revoke URL after a short delay
          setTimeout(() => URL.revokeObjectURL(downloadURL), 1000);
      }, error => {
          this.snack.open('Failed to download workplan', 'Dismiss', { duration: 3000 });
      });
    }
  }
  

  // ---- date helpers ----
  function iso(d: Date) { return d.toISOString(); }
  function percent(n: number, d: number) { return Math.round((n / Math.max(1, d)) * 100); }
  function isoD(month: number, day: number) { // month: 1-12
    const y = new Date().getFullYear();
    return iso(new Date(y, month - 1, day));
  }


type PanelState = Record<number, boolean>;